import {
  Drawer,
  type PaperProps,
  type SlideProps,
  type SxProps,
  type Theme,
  useTheme,
} from '@mui/material';
import { type ReactElement, type ReactNode, useMemo, useRef } from 'react';

import { type SlabDrawerCloseHandler, SlabDrawerContext } from './SlabDrawerContext';

type SlabDrawerProps = {
  isOpen: boolean;
  close: () => void;
  /** @default undefined */
  slideProps?: Partial<SlideProps>;
  /** @default { sx: { width: '60rem', maxWidth: '100%' } } */
  paperProps?: Partial<PaperProps>;
  /** @default { zIndex: theme.zIndex.drawer + 2 } */
  sx?: SxProps<Theme>;
  children: ReactNode;
};

/** Right-anchored drawer that unmounts its content while closed, so the content fires no requests.
 * Esc and outside-click call `close`, or the handler a `DrawerCloseHandler` inside registered. */
export const SlabDrawer = ({
  isOpen,
  close,
  slideProps = undefined,
  paperProps = { sx: { width: '60rem', maxWidth: '100%' } },
  sx,
  children,
}: SlabDrawerProps): ReactElement => {
  const theme = useTheme();

  const sxVal = sx !== undefined ? sx : { zIndex: theme.zIndex.drawer + 2 };

  const closeHandlerRef = useRef<SlabDrawerCloseHandler | null>(null);
  const contextValue = useMemo(
    () => ({
      registerCloseHandler: (handler: SlabDrawerCloseHandler | null): void => {
        closeHandlerRef.current = handler;
      },
    }),
    [],
  );
  const notifyUserInteraction = (): void => closeHandlerRef.current?.onUserInteraction();

  // Content is nulled instead of the Drawer to keep its open/close animation. Focus isn't
  // restored: it left the trigger button stuck in its focus-visible state after Esc.
  return (
    <Drawer
      onClose={() => (closeHandlerRef.current?.onClose ?? close)()}
      disableRestoreFocus
      anchor='right'
      open={isOpen}
      SlideProps={slideProps}
      PaperProps={{
        ...paperProps,
        onPointerDownCapture: notifyUserInteraction,
        onKeyDownCapture: notifyUserInteraction,
      }}
      sx={sxVal}
    >
      <SlabDrawerContext.Provider value={contextValue}>
        {!isOpen ? null : children}
      </SlabDrawerContext.Provider>
    </Drawer>
  );
};
